import { isValidPairingCode, normalizePairingCode, type KioskConfig } from '@foyer/shared';
import { useState } from 'react';
import { Button, Icon, Spinner } from '../components/ui.js';
import type { Strings } from '../i18n/index.js';
import * as api from '../lib/api.js';

/**
 * Device enrolment.
 *
 * The only screen a brand-new tablet shows. The visitor never sees it: once
 * paired, the device token is stored and the kiosk boots straight into the
 * welcome screen, including after a reboot. Pairing is deliberately the only
 * way in - there is no manual URL, tenant picker or API key entry that somebody
 * could get wrong (or abuse) on site.
 */

interface Props {
  strings: Strings;
  onPaired: (config: KioskConfig) => void;
}

export function Pairing({ strings, onPaired }: Props): JSX.Element {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalized = normalizePairingCode(code);
  const ready = isValidPairingCode(normalized);

  async function submit(): Promise<void> {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      const config = await api.pair(normalized);
      onPaired(config);
    } catch (err) {
      setError(err instanceof api.ApiError && err.status !== 0 ? strings.pairError : strings.offlineHint);
      setBusy(false);
    }
  }

  return (
    <div className="pair-shell">
      <div className="pair-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <span className="choice-icon" style={{ width: 46, height: 46, borderRadius: 13 }}>
            <Icon name="shield" size={24} />
          </span>
          <div>
            <h1 className="section-title" style={{ margin: 0 }}>
              {strings.pairTitle}
            </h1>
            <p className="brand-sub" style={{ margin: 0 }}>
              Foyer {api.APP_VERSION}
            </p>
          </div>
        </div>

        <p className="subline" style={{ fontSize: '1rem', marginBottom: 20 }}>
          {strings.pairHint}
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <label className="visually-hidden" htmlFor="pairing-code">
            {strings.pairTitle}
          </label>
          <input
            id="pairing-code"
            className="pair-input"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.slice(0, 12));
              setError(null);
            }}
            placeholder={strings.pairPlaceholder}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            inputMode="text"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'pair-error' : undefined}
            autoFocus
          />

          {error ? (
            <div
              className="error"
              id="pair-error"
              role="alert"
              style={{ justifyContent: 'center', marginTop: 14 }}
            >
              <Icon name="alert" size={16} />
              {error}
            </div>
          ) : null}

          <div style={{ marginTop: 22 }}>
            <Button type="submit" block disabled={!ready || busy}>
              {busy ? <Spinner /> : null}
              {busy ? strings.loading : strings.pairButton}
            </Button>
          </div>
        </form>

        <p className="help" style={{ marginTop: 18, textAlign: 'center' }}>
          {strings.pairCodeHint}
        </p>
      </div>
    </div>
  );
}
